import { API_ENDPOINT } from '../constant/constant.ts'
import type { useLoginStore as useLoginStoreType } from '@/store/LoginStore.ts'

// 토큰 갱신 중인지 확인하는 플래그 및 대기 큐
let isRefreshing = false
let failedQueue: Array<{
    resolve: (value?: any) => void
    reject: (reason?: any) => void
}> = []

function flushFailedQueue(error: any = null) {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve()
        }
    })
    failedQueue = []
}

export interface ApiRes<T> {
    data: T
    code?: string | number
    message?: string
}

/**
 * 모든 API 요청의 베이스가 되는 인터셉터 함수
 */
const baseRequest = async <T>(
    url: string,
    options: RequestInit = {},
    isMocking: boolean = false
): Promise<ApiRes<T>> => {
    const endpoint = isMocking ? url : `${API_ENDPOINT}${url}`
    const { useLoginStore } = (await import('@/store/LoginStore.ts')) as { useLoginStore: typeof useLoginStoreType }

    // 기본 설정: 모든 요청에 쿠키 포함 및 인증 헤더 추가
    const defaultHeaders: HeadersInit = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }

    const accessToken = useLoginStore.getState().accessToken
    if (accessToken) {
        (defaultHeaders as any)['Authorization'] = `Bearer ${accessToken}`
    }

    const config: RequestInit = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
        credentials: 'include' // Refresh Token(HttpOnly Cookie) 전송을 위해 필수
    }

    try {
        let response = await fetch(endpoint, config)

        // 401 Unauthorized 처리 (로그인/새로고침/로그아웃 API 제외)
        if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh') && !url.includes('/auth/logout')) {
            if (isRefreshing) {
                // 이미 다른 요청이 갱신 중이면 큐에서 대기
                await new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
                // 갱신 완료 후 재시도 (최신 토큰 주입)
                const newAccessToken = useLoginStore.getState().accessToken
                if (newAccessToken) {
                    (config.headers as any)['Authorization'] = `Bearer ${newAccessToken}`
                }
                response = await fetch(endpoint, config)
            } else {
                isRefreshing = true
                try {
                    // Silent Refresh 시도
                    const { apiRefresh } = await import('@/api/auth/refresh.ts')
                    const newAccessToken = await apiRefresh()

                    if (newAccessToken) {
                        // Zustand State 업데이트
                        await useLoginStore.getState().login('', newAccessToken);

                        // 대기 중인 요청들 해제
                        flushFailedQueue(null);

                        // 현재 요청 재시도
                        (config.headers as any)['Authorization'] = `Bearer ${newAccessToken}`
                        response = await fetch(endpoint, config)
                    } else {
                        throw new Error('Refresh Failed')
                    }
                } catch (error) {
                    flushFailedQueue(error)
                    // 갱신 실패 시 강제 로그아웃
                    await useLoginStore.getState().logout()
                    window.location.href = '/login'
                    throw error
                } finally {
                    isRefreshing = false
                }
            }
        }

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        // 응답 컨텐츠 타입 확인
        const contentType = response.headers.get('content-type') || ''
        const isBinaryType =
            contentType.includes('application/vnd.openxmlformats-officedocument') || // Modern Excel, Word, etc.
            contentType.includes('application/vnd.ms-excel') || // Old Excel
            contentType.includes('application/pdf') ||
            contentType.includes('image/') ||
            contentType.includes('application/octet-stream')

        if (isBinaryType) {
            // 명확한 바이너리 파일인 경우 (예: 엑셀 Blob)
            const blob = await response.blob()
            return { data: blob as unknown as T, code: 200, message: 'success' }
        }

        // 그 외에는 기존 코드와 동일하게 JSON으로 처리 시도
        const res = await response.json()
        // 백엔드 응답 규격 처리 (표준화)
        if (Array.isArray(res)) {
            return { data: res as T, code: 200, message: 'success' }
        } else if (res.data !== undefined) {
            return { data: res.data, code: res.code, message: res.message }
        } else {
            return { data: res as T, code: 200, message: 'success' }
        }
    } catch (error) {
        throw error
    }
}

export const apiDownload = async (url: string): Promise<Blob> => {
    const res = await baseRequest<Blob>(url, { method: 'GET' })
    return res.data
}

export const apiGet = async <T>(url: string, isMocking: boolean = false): Promise<ApiRes<T>> => {
    return baseRequest<T>(url, { method: 'GET' }, isMocking)
}

export const apiPost = async <T>(url: string, body?: any, isMocking: boolean = false): Promise<ApiRes<T>> => {
    return baseRequest<T>(url, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined
    }, isMocking)
}

export const apiPatch = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    return baseRequest<T>(url, {
        method: 'PATCH',
        body: body ? JSON.stringify(body) : undefined
    })
}

export const apiDelete = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    return baseRequest<T>(url, {
        method: 'DELETE',
        body: body ? JSON.stringify(body) : undefined
    })
}

export const apiPostFormData = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    const formData = new FormData()
    if (body) {
        Object.keys(body).forEach(key => formData.append(key, body[key]))
    }

    return baseRequest<T>(url, {
        method: 'POST',
        headers: {
            'Accept': 'application/x-www-form-urlencoded',
            // Content-Type은 fetch가 FormData를 보고 자동으로 boundary를 브라우저가 설정하게 해야 하므로 제거
        },
        body: formData
    })
}

export const apiPatchFormData = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    const formData = new FormData()
    if (body) {
        Object.keys(body).forEach(key => formData.append(key, body[key]))
    }

    return baseRequest<T>(url, {
        method: 'PATCH',
        headers: { 'Accept': 'application/x-www-form-urlencoded' },
        body: formData
    })
}
